import React, { useEffect, useMemo, useCallback, useContext } from 'react';
import { useTheme } from 'next-themes';
import crossFilterManager from '../../utility/crossFilterManager';

// ── Per-dashboard dark mode ────────────────────────────────────────────────────
//
// Strategy: rather than trying to scope CSS locally (which can't reach body/root),
// we apply each dashboard's stored theme preference to the GLOBAL next-themes state
// when the user navigates to that dashboard.  Because the global <html data-color-mode>
// attribute changes, every element on the page (background, nav, sidebar, widgets)
// responds correctly.
//
// Storage keys:
//   goodera-base-color-mode            — user's "neutral" preference set by the side nav
//   goodera-dashboard-color-mode-{id}  — per-dashboard override set by the top nav
//
// Priority rules:
//   • Side-nav toggle → sets base preference + clears ALL per-dashboard overrides
//   • Top-nav toggle  → sets this dashboard's override, leaves others unchanged
//   • On navigation   → applies dashboard override if stored, else applies base preference

const DashboardModeContext = React.createContext(null);

const STORAGE_PREFIX = 'goodera-dashboard-color-mode-';
const BASE_KEY = 'goodera-base-color-mode';
const GLOBAL_RESET_EVENT = 'goodera-global-mode-reset';

const clearAllDashboardOverrides = (newBaseMode) => {
	try {
		if (newBaseMode) localStorage.setItem(BASE_KEY, newBaseMode);
		Object.keys(localStorage)
			.filter(k => k.startsWith(STORAGE_PREFIX))
			.forEach(k => localStorage.removeItem(k));
	} catch { /* ignore */ }
	window.dispatchEvent(new CustomEvent(GLOBAL_RESET_EVENT));
};

export const DashboardColorModeProvider = ({ dashboardId, children }) => {
	const { resolvedTheme, setTheme } = useTheme();
	const storageKey = `${STORAGE_PREFIX}${dashboardId}`;

	// When entering a dashboard, apply its stored preference.
	// Falls back to the base preference (last side-nav setting), then leaves theme unchanged.
	useEffect(() => {
		if (!dashboardId) return;
		try {
			const dashboardPref = localStorage.getItem(storageKey);
			if (dashboardPref) {
				setTheme(dashboardPref);
				return;
			}
			const basePref = localStorage.getItem(BASE_KEY);
			if (basePref) setTheme(basePref);
		} catch { /* ignore */ }
	// setTheme and resolvedTheme intentionally excluded — this effect is only for navigation
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storageKey, dashboardId]);

	// When the side-nav global reset fires, clear this dashboard's stored preference.
	// (The theme itself was already changed by the side-nav toggle call.)
	useEffect(() => {
		const handler = () => {
			try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
		};
		window.addEventListener(GLOBAL_RESET_EVENT, handler);
		return () => window.removeEventListener(GLOBAL_RESET_EVENT, handler);
	}, [storageKey]);

	const toggleDashboardMode = useCallback(() => {
		// Before the first dashboard-specific toggle, record the current global as the base
		// so dashboards without an override can restore to it on navigation.
		try {
			if (!localStorage.getItem(BASE_KEY) && resolvedTheme) {
				localStorage.setItem(BASE_KEY, resolvedTheme);
			}
		} catch { /* ignore */ }

		const newMode = (resolvedTheme || 'light') === 'dark' ? 'light' : 'dark';
		setTheme(newMode);
		try { localStorage.setItem(storageKey, newMode); } catch { /* ignore */ }
	}, [resolvedTheme, setTheme, storageKey]);

	const ctx = useMemo(() => ({ toggleDashboardMode }), [toggleDashboardMode]);

	return (
		<DashboardModeContext.Provider value={ctx}>
			{children}
		</DashboardModeContext.Provider>
	);
};

/**
 * ColorModeBridge
 * ------------------------------------------------------------------
 * Thin bridge component that sits INSIDE next-themes' <ThemeProvider>
 * and handles the two side-effects that next-themes does NOT manage:
 *   1) crossFilterManager sync
 *   2) 'colorModeChanged' CustomEvent for class components
 *
 * Placed in index.js:
 *   <NextThemesProvider attribute="data-color-mode" storageKey={getColorModeStorageKey()} ...>
 *     <ColorModeBridge>
 *       ...app...
 *     </ColorModeBridge>
 *   </NextThemesProvider>
 */
export const ColorModeBridge = ({ children }) => {
	const { resolvedTheme } = useTheme();

	// Connect dark mode state to cross-filter manager
	useEffect(() => {
		crossFilterManager.setDarkModeProvider(() => ({ isDark: resolvedTheme === 'dark' }));
	}, [resolvedTheme]);

	// Fire DOM event for class components (Preview, PreviewNPublish)
	useEffect(() => {
		window.dispatchEvent(new CustomEvent('colorModeChanged', { detail: { mode: resolvedTheme } }));
	}, [resolvedTheme]);

	return children;
};

/**
 * useColorMode  (DROP-IN REPLACEMENT — same API as before)
 * ------------------------------------------------------------------
 * Returns { mode, isDark, toggleMode, setMode } — identical shape
 * to the old ColorModeContext so existing consumers need ZERO changes.
 *
 * When called inside a DashboardColorModeProvider (top nav, dashboard widgets),
 * toggleMode applies the per-dashboard logic.
 * When called outside (side nav, app chrome), toggleMode applies to the global
 * theme AND clears all per-dashboard overrides so the side nav wins everywhere.
 */
export const useColorMode = () => {
	let themeCtx;
	try {
		themeCtx = useTheme();
	} catch (_) {
		// Fallback if next-themes provider not available (tests, storybook)
		return {
			mode: 'light',
			isDark: false,
			toggleMode: () => {},
			setMode: () => {},
		};
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const dashboardCtx = useContext(DashboardModeContext);

	const { resolvedTheme, setTheme } = themeCtx;
	const mode = resolvedTheme || 'light';

	const toggleMode = useCallback(() => {
		if (dashboardCtx) {
			// Inside a dashboard — per-dashboard toggle
			dashboardCtx.toggleDashboardMode();
		} else {
			// Outside a dashboard (side nav, etc.) — global toggle, clears all overrides
			const newMode = mode === 'light' ? 'dark' : 'light';
			setTheme(newMode);
			clearAllDashboardOverrides(newMode);
		}
	}, [dashboardCtx, mode, setTheme]);

	const setMode = useCallback((val) => {
		setTheme(typeof val === 'function' ? val(mode) : val);
	}, [mode, setTheme]);

	return useMemo(
		() => ({ mode, isDark: mode === 'dark', toggleMode, setMode }),
		[mode, toggleMode, setMode]
	);
};

/**
 * useGlobalColorMode
 * Always reads/writes the global next-themes state, even when rendered inside
 * a DashboardColorModeProvider. Use for app-level controls (e.g. side nav
 * settings toggle) that should never be scoped to a specific dashboard.
 */
export const useGlobalColorMode = () => {
	let themeCtx;
	try {
		themeCtx = useTheme();
	} catch (_) {
		return { mode: 'light', isDark: false, toggleMode: () => {}, setMode: () => {} };
	}
	const { resolvedTheme, setTheme } = themeCtx;
	const mode = resolvedTheme || 'light';
	const toggleMode = useCallback(() => {
		const newMode = mode === 'light' ? 'dark' : 'light';
		setTheme(newMode);
		clearAllDashboardOverrides(newMode);
	}, [mode, setTheme]);
	const setMode = useCallback((val) => {
		setTheme(typeof val === 'function' ? val(mode) : val);
	}, [mode, setTheme]);
	return useMemo(
		() => ({ mode, isDark: mode === 'dark', toggleMode, setMode }),
		[mode, toggleMode, setMode]
	);
};
